import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import Stepper from "@/components/Stepper"
import { useSelfQuery } from "@/data/queries"
import { useCreateBloodGlucoseFormMutation } from "@/data/queries/measurementQueries"
import { useFoodLog, useResetFoodLog } from "@/data/store"
import { toMedicationLog, type MedicationTaken } from "@/model/BloodGlucoseForm"
import type { BloodGlucoseFormContext } from "@/model/BloodGlucoseFormContext"
import { textForFormContext } from "@/model/BloodGlucoseFormContext"
import { hasPrescribedMedicationForContext } from "@/model/Medication"
import { ROUTES } from "@/routes"
import { nowIso } from "@/util/date-utils"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"
import FoodLogDateTimePickerStep from "./steps/FoodLogDateTimePickerStep"
import MealContextPickerStep from "./steps/MealContextPickerStep"
import MealDetailsStep from "./steps/MealDetailsStep"
import MedicationQuestionStep from "./steps/MedicationQuestionStep"
import NoteScreenStep from "./steps/NoteScreenStep"

const MEAL_CONTENT_TITLE = "What did you eat and drink during your meal?"

const AddFoodLogPage = () => {
  const navigate = useNavigate()
  const { data: self } = useSelfQuery()
  const patientId = self?.id ?? ""
  const { mutateAsync: createForm, isPending } = useCreateBloodGlucoseFormMutation({ patientId })

  const [foodLog, setFoodLog] = useFoodLog()
  const resetFoodLog = useResetFoodLog()
  const [currentStep, setCurrentStep] = useState(0)

  useEffect(() => {
    resetFoodLog()
  }, [resetFoodLog])

  const goNext = () => setCurrentStep((step) => step + 1)

  const onChangeDateTime = (dateTime: string) => {
    setFoodLog((current) => ({ ...current, dateTime }))
    goNext()
  }

  const onChangeContext = (value: BloodGlucoseFormContext) => {
    setFoodLog((current) => ({ ...current, context: value }))
    goNext()
  }

  const onMedication = (values: { medicationTaken: MedicationTaken | null; note: string }) => {
    setFoodLog((current) => ({
      ...current,
      medicationTaken: values.medicationTaken,
      medicationNote: values.note,
    }))
    goNext()
  }

  const onMealDetails = (values: {
    mealContents: string
    carbsInGrams: string | number
    proteinInGrams: string | number
  }) => {
    setFoodLog((current) => ({ ...current, ...values }))
    goNext()
  }

  const onNotes = async ({ notes }: { notes: string }) => {
    if (!foodLog.context) return
    setFoodLog((current) => ({ ...current, notes }))
    try {
      await createForm({
        patientId,
        form: {
          context: foodLog.context,
          dateTime: foodLog.dateTime ?? nowIso(),
          notes: notes || null,
          mealContents: foodLog.mealContents || null,
          carbsInGrams: foodLog.carbsInGrams === "" ? null : Number(foodLog.carbsInGrams),
          proteinInGrams: foodLog.proteinInGrams === "" ? null : Number(foodLog.proteinInGrams),
          medications: toMedicationLog(foodLog.medicationTaken, foodLog.medicationNote),
          reasons: null,
          readingId: null,
        },
      })
      resetFoodLog()
      navigate(`${ROUTES.DASHBOARD}?tab=meal-logs`)
    } catch {
      toast.error("Failed to save food log. Please try again.")
    }
  }

  if (!self) {
    return (
      <Page showAppbar={false}>
        <div className="flex items-center justify-center p-8">
          <Spinner />
        </div>
      </Page>
    )
  }

  const contextLabel = foodLog.context ? textForFormContext(foodLog.context) : ""

  return (
    <Page showAppbar={false}>
      <Stepper
        currentStep={currentStep}
        steps={[
          <FoodLogDateTimePickerStep
            key="dateTime"
            dateTime={foodLog.dateTime}
            onChangeDateTime={onChangeDateTime}
            onBack={() => navigate(ROUTES.DASHBOARD)}
          />,
          <MealContextPickerStep
            key="context"
            context={foodLog.context}
            onChangeMealContext={onChangeContext}
            onBack={() => setCurrentStep((step) => Math.max(0, step - 1))}
          />,
          ...(foodLog.context &&
          hasPrescribedMedicationForContext(self.prescribedDiabetesMedications, foodLog.context)
            ? [
                <MedicationQuestionStep
                  key="medication"
                  medicationTaken={foodLog.medicationTaken}
                  note={foodLog.medicationNote}
                  onSubmit={onMedication}
                  onBack={() => setCurrentStep((step) => Math.max(0, step - 1))}
                />,
              ]
            : []),
          <MealDetailsStep
            key="details"
            context={contextLabel}
            mealContentTitle={MEAL_CONTENT_TITLE}
            initialValues={{
              mealContents: foodLog.mealContents,
              carbsInGrams: foodLog.carbsInGrams,
              proteinInGrams: foodLog.proteinInGrams,
            }}
            onSubmit={onMealDetails}
            onBack={() => setCurrentStep((step) => Math.max(0, step - 1))}
          />,
          <NoteScreenStep
            key="notes"
            context={contextLabel}
            initialValues={{ notes: foodLog.notes }}
            isLoading={isPending}
            onSubmit={onNotes}
            onBack={() => setCurrentStep((step) => Math.max(0, step - 1))}
          />,
        ]}
      />
    </Page>
  )
}

export default AddFoodLogPage
